/**
 * useEdgeShapeHandlesLayer - Hook para renderizar handles de shape points
 *
 * @version 1.0
 * @created 2026-04-11
 * @module core/spatial/hooks
 *
 * @description
 * Renderiza handles (círculos pequeños) para los vértices internos
 * de un edge en modo edición geométrica.
 *
 * - Muestra handles solo para coordinates[1..n-2]
 * - NO muestra handles para extremos (son nodos topológicos)
 * - Diferencia visual clara vs nodos reales
 *
 * @example
 * ```typescript
 * useEdgeShapeHandlesLayer({
 *   edgeId: state.geometryEditEdgeId,
 * });
 * ```
 */

import { useEffect, useRef } from 'react';
import type { GeoJSONSource } from 'maplibre-gl';
import { mapEngine, isMapReadyForSources } from '../../map-engine/MapEngineService';
import { canonicalNetworkStore } from '../stores/canonical-network-store';

const SOURCE_ID = 'edge-shape-handles';
const LAYER_ID = 'edge-shape-handles';

// PERF: Source/layer separado para preview del edge durante drag
// Evita actualizar canonical-network (19k features) en cada frame
const PREVIEW_SOURCE_ID = 'edge-geometry-preview';
const PREVIEW_LAYER_ID = 'edge-geometry-preview';

export interface UseEdgeShapeHandlesLayerConfig {
  /** ID del edge en edición geométrica (null = no mostrar handles) */
  edgeId: string | null;
}

/**
 * Hook para renderizar handles de shape points internos
 */
export function useEdgeShapeHandlesLayer({ edgeId }: UseEdgeShapeHandlesLayerConfig): void {
  const sourceAddedRef = useRef(false);
  const layerAddedRef = useRef(false);

  useEffect(() => {
    console.log('[useEdgeShapeHandlesLayer] 🎬 Effect running, edgeId:', edgeId);

    const map = mapEngine.getMap();
    if (!map) {
      console.log('[useEdgeShapeHandlesLayer] ⚠️ Map not available, skipping');
      return;
    }

    // Función para crear source y layer si no existen
    const ensureSourceAndLayer = (): void => {
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        sourceAddedRef.current = true;
      }

      if (!map.getLayer(LAYER_ID)) {
        map.addLayer({
          id: LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          minzoom: 15, // UX: Shape points solo visibles a zoom >= 15 (igual que nodos blancos)
          paint: {
            'circle-radius': 5, // UX: Más pequeño que nodos blancos (6px)
            'circle-color': '#FF6B00', // Naranja para shape points
            'circle-stroke-width': 2,
            'circle-stroke-color': '#FFFFFF',
            'circle-opacity': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              1,
              0.9,
            ],
          },
        });
        layerAddedRef.current = true;
      }

      // PERF: Preview source/layer para el edge durante drag
      if (!map.getSource(PREVIEW_SOURCE_ID)) {
        map.addSource(PREVIEW_SOURCE_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
      }

      if (!map.getLayer(PREVIEW_LAYER_ID)) {
        map.addLayer({
          id: PREVIEW_LAYER_ID,
          type: 'line',
          source: PREVIEW_SOURCE_ID,
          paint: {
            'line-color': '#00FFFF', // Cyan para preview
            'line-width': 4,
            'line-opacity': 1,
          },
        });
      }
    };

    // Función para actualizar features (solo se ejecuta al entrar/salir de modo edición)
    const updateFeatures = (): void => {
      console.log('[useEdgeShapeHandlesLayer] 📝 updateFeatures called, edgeId:', edgeId);

      const source = map.getSource(SOURCE_ID) as GeoJSONSource;
      if (!source) {
        console.log('[useEdgeShapeHandlesLayer] ⚠️ Source not found:', SOURCE_ID);
        return;
      }

      if (!edgeId) {
        console.log('[useEdgeShapeHandlesLayer] ⏭️ No edgeId, clearing handles');
        source.setData({ type: 'FeatureCollection', features: [] });
        return;
      }

      const edgeFeature = canonicalNetworkStore.getEdge(edgeId);
      if (!edgeFeature) {
        console.log('[useEdgeShapeHandlesLayer] ⚠️ Edge not found in store:', edgeId);
        source.setData({ type: 'FeatureCollection', features: [] });
        return;
      }

      const coords = edgeFeature.geometry.coordinates;
      const shapePointFeatures: GeoJSON.Feature[] = [];

      // Generar handles para vértices internos (excluyendo extremos)
      for (let i = 1; i < coords.length - 1; i++) {
        shapePointFeatures.push({
          type: 'Feature',
          id: `${edgeId}-sp-${i}`,
          geometry: { type: 'Point', coordinates: coords[i] },
          properties: { edgeId, shapePointIndex: i, isShapePoint: true },
        });
      }

      source.setData({ type: 'FeatureCollection', features: shapePointFeatures });
      console.log('[useEdgeShapeHandlesLayer] Handles initialized:', shapePointFeatures.length, 'points');
    };

    // Crear source/layer cuando el mapa está listo
    // NOTA: map.isStyleLoaded() puede devolver false incluso cuando el estilo
    // ya está cargado. Verificamos si el source existe como prueba más confiable.
    const styleLoaded = map.isStyleLoaded();
    const sourceExists = !!map.getSource(SOURCE_ID);
    console.log('[useEdgeShapeHandlesLayer] Style loaded:', styleLoaded, 'Source exists:', sourceExists);

    // Si el source ya existe O el estilo está cargado, procedemos inmediatamente
    if (styleLoaded || sourceExists) {
      ensureSourceAndLayer();
      updateFeatures();
    } else {
      console.log('[useEdgeShapeHandlesLayer] ⏳ Waiting for style to load...');
      map.once('load', () => {
        console.log('[useEdgeShapeHandlesLayer] ✅ Style loaded, creating handles');
        ensureSourceAndLayer();
        updateFeatures();
      });
    }

    // NOTA: No usar interval - las actualizaciones durante drag se hacen
    // cuando el store cambia, no por polling
    // El interval anterior generaba logs spam y era ineficiente

    // Cleanup: limpiar sources cuando edgeId cambia o componente desmonta
    return () => {
      console.log('[useEdgeShapeHandlesLayer] 🧹 Effect cleanup, clearing sources');
      const currentMap = mapEngine.getMap();
      if (currentMap && isMapReadyForSources(currentMap)) {
        const handlesSource = currentMap.getSource(SOURCE_ID) as GeoJSONSource;
        if (handlesSource) {
          handlesSource.setData({ type: 'FeatureCollection', features: [] });
        }
        const previewSource = currentMap.getSource(PREVIEW_SOURCE_ID) as GeoJSONSource;
        if (previewSource) {
          previewSource.setData({ type: 'FeatureCollection', features: [] });
        }
      }
    };
  }, [edgeId]);

  // Cleanup: remover source y layer al desmontar
  useEffect(() => {
    return () => {
      const map = mapEngine.getMap();
      if (!map) return;

      try {
        // Cleanup handles
        if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
        // Cleanup preview
        if (map.getLayer(PREVIEW_LAYER_ID)) map.removeLayer(PREVIEW_LAYER_ID);
        if (map.getSource(PREVIEW_SOURCE_ID)) map.removeSource(PREVIEW_SOURCE_ID);
      } catch {
        // Ignore cleanup errors
      }
    };
  }, []);
}
